/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { HandlerGroupFactoryDeps, HandlerParams } from 'src/core/types/handler';
import { batchPromises } from 'src/core/utils/batchPromises';
import { QnaFileSourceDto } from 'src/qnaMaker/appShell/qnaMakerManageSourcesPage/QnaMakerAddFileSourceModal';
import { QnaUrlSourceDto } from 'src/qnaMaker/appShell/qnaMakerManageSourcesPage/QnaMakerAddUrlSourceModal';
import { getQnaMakerCommonService } from 'src/qnaMaker/core/services/qnaMakerCommonService';
import { getQnaMakerSharePointService } from 'src/qnaMaker/core/services/sharePoint/qnaMakerSharePointService';
import { getQnaSourcesService } from 'src/qnaMaker/core/services/sources/qnaMakerSourcesService';
import { QnaMakerSourcesSwaggers } from 'src/qnaMaker/core/services/sources/qnaMakerSourcesSwaggers';
import { QnaMakerSourceStore } from 'src/qnaMaker/core/stores/QnaMakerSourceStore';
import { QnaPatchOperation } from 'src/qnaMaker/core/types/qnaPatchOperation';
import {
    qnaSourceStoreToQnaSourceSwagger,
    qnaSourceSwaggerToQnaSourceStore
} from 'src/qnaMaker/modules/handlers/sources/qnaMakerSourceMappers';
import { chitchatUrlMap, QnaSourceChitchatTypes } from 'src/qnaMaker/modules/utils/chitchatTypeToFileUrl';

export type QnaMakerSourcesHandlerGroup = ReturnType<typeof createQnaMakerSourcesHandlerGroup>;

export const createQnaMakerSourcesHandlerGroup = (dependencies: HandlerGroupFactoryDeps) => {
    const { rootStore } = dependencies;

    const getQnaMakerSources = async (params?: HandlerParams) => {
        const { context } = params;
        const { currentQnaMakerProjectStore } = rootStore;
        const { tracker } = currentQnaMakerProjectStore.sourcesStoreHouse;

        if (!context.forceRefresh && (tracker.isRunning || tracker.isCompleted)) {
            return;
        }

        try {
            tracker.setState('running');

            const swaggers = await getQnaSourcesService({ context }).getSources({
                projectId: currentQnaMakerProjectStore.id
            });
            const stores = swaggers.map(qnaSourceSwaggerToQnaSourceStore);
            currentQnaMakerProjectStore.sourcesStoreHouse.setItems(stores);

            tracker.setState('completed');
        } catch (e) {
            tracker.setState('failed', <Error>e);
        }
    };

    const addQnaMakerUrlSources = async ({ sources, context }: HandlerParams<{ sources: QnaUrlSourceDto[] }>) => {
        const { currentQnaMakerProjectStore } = rootStore;

        const swaggers: Partial<QnaMakerSourcesSwaggers>[] = [];

        for (const source of sources) {
            if (source.url.match(/https:\/\/.+\.sharepoint.com\/.+/i)) {
                const driveItem = await getQnaMakerSharePointService().getSharePointSharedItem({
                    fileUrl: source.url
                });

                swaggers.push({
                    source: driveItem.name,
                    displayName: source.name,
                    sourceUri: driveItem['@microsoft.graph.downloadUrl'],
                    sourceKind: 'file',
                    contentStructureKind: source.isUnstructured ? 'unstructured' : undefined
                });
            } else {
                swaggers.push({
                    displayName: source.name,
                    sourceUri: source.url,
                    sourceKind: 'url',
                    contentStructureKind: source.isUnstructured ? 'unstructured' : undefined
                });
            }
        }

        const sourcesWithOperation: QnaPatchOperation<Partial<QnaMakerSourcesSwaggers>>[] = swaggers.map(src => ({
            op: 'add',
            value: src
        }));

        await getQnaSourcesService({ context }).updateSources({ sourcesWithOperation, projectId: currentQnaMakerProjectStore.id });

        await getQnaMakerSources({ context: { ...context, forceRefresh: true } });
    };

    const addQnaMakerChitchatSources = async ({ chitchatType, context }: HandlerParams<{ chitchatType: QnaSourceChitchatTypes }>) => {
        const { currentQnaMakerProjectStore } = rootStore;

        const url = chitchatUrlMap[currentQnaMakerProjectStore.language][chitchatType];
        const swagger: Partial<QnaMakerSourcesSwaggers> = {
            source: url.split('/').pop(),
            displayName: url
                .split('/')
                .pop()
                .split('.')[0],
            sourceUri: url,
            sourceKind: 'file'
        };
        const sourcesWithOperation: QnaPatchOperation<Partial<QnaMakerSourcesSwaggers>>[] = [{ op: 'add', value: swagger }];

        await getQnaSourcesService({ context }).updateSources({ sourcesWithOperation, projectId: currentQnaMakerProjectStore.id });

        await getQnaMakerSources({ context: { ...context, forceRefresh: true } });
    };

    const addQnaMakerFileSources = async ({ sources, context }: HandlerParams<{ sources: QnaFileSourceDto[] }>) => {
        const { currentQnaMakerProjectStore } = rootStore;

        const batchUploadFiles = sources.map(async src => {
            try {
                return await getQnaMakerCommonService({ context }).uploadFiles({ file: src.file });
            } catch (error) {
                // MI error, try with key
                try {
                    const sasUri = await getQnaMakerCommonService({ context }).getSASUri();
                    return await getQnaMakerCommonService({ context }).uploadFilesWithKey({
                        sasEndpoint: sasUri.endpoint,
                        sasToken: sasUri.token,
                        file: src.file
                    });
                } catch (keyError) {
                    // Failed with both upload methods
                    console.error('Failed to upload file using key or MI:', keyError);
                    throw error;
                }
            }
        });
        const { results } = await batchPromises(batchUploadFiles);

        const sourcesWithOperation: QnaPatchOperation<Partial<QnaMakerSourcesSwaggers>>[] = [];

        results.forEach((res, index) => {
            if (res.status === 'success') {
                sourcesWithOperation.push({
                    op: 'add',
                    value: {
                        source: sources[index].file.name,
                        displayName: sources[index].displayName,
                        sourceUri: res.success,
                        sourceKind: 'file',
                        contentStructureKind: sources[index].isUnstructured ? 'unstructured' : undefined
                    }
                });
            }
        });

        if (!results.filter(res => res.success).length) {
            return;
        }

        await getQnaSourcesService({ context }).updateSources({ sourcesWithOperation, projectId: currentQnaMakerProjectStore.id });

        await getQnaMakerSources({ context: { ...context, forceRefresh: true } });
    };

    const deleteQnaMakerSources = async ({ sourcesIds, context }: HandlerParams<{ sourcesIds: string[] }>) => {
        const { currentQnaMakerProjectStore } = rootStore;

        const sources = sourcesIds.map(id => currentQnaMakerProjectStore.sourcesStoreHouse.findById(id));
        const swaggers = sources.map(src => ({ ...qnaSourceStoreToQnaSourceSwagger(src), lastUpdatedDateTime: undefined }));
        const sourcesWithOperation: QnaPatchOperation<QnaMakerSourcesSwaggers>[] = swaggers.map(src => ({ op: 'delete', value: src }));

        if (!swaggers.length) {
            return;
        }

        await getQnaSourcesService({ context }).updateSources({ sourcesWithOperation, projectId: currentQnaMakerProjectStore.id });

        currentQnaMakerProjectStore.sourcesStoreHouse.batchRemoveByIds(sourcesIds);
    };

    const renameQnaMakerSources = async ({ sourceId, newName, context }: HandlerParams<{ sourceId: string; newName: string }>) => {
        const { currentQnaMakerProjectStore } = rootStore;
        const source = currentQnaMakerProjectStore.sourcesStoreHouse.findById(sourceId);

        const swagger: Partial<QnaMakerSourcesSwaggers> = {
            ...qnaSourceStoreToQnaSourceSwagger(source),
            displayName: newName,
            lastUpdatedDateTime: undefined
        };

        await getQnaSourcesService({ context }).updateSources({
            sourcesWithOperation: [{ op: 'replace', value: swagger }],
            projectId: currentQnaMakerProjectStore.id
        });

        currentQnaMakerProjectStore.sourcesStoreHouse.findById(source.id).renameSource(newName);
    };

    const refreshQnaMakerSourceURLs = async ({ sources, context }: HandlerParams<{ sources: QnaMakerSourceStore[] }>) => {
        const { currentQnaMakerProjectStore } = rootStore;

        const swaggers = sources.map(src => ({ ...qnaSourceStoreToQnaSourceSwagger(src), refresh: true, lastUpdatedDateTime: undefined }));
        const sourcesWithOperation: QnaPatchOperation<QnaMakerSourcesSwaggers>[] = swaggers.map(src => ({ op: 'replace', value: src }));

        if (!swaggers.length) {
            return;
        }

        await getQnaSourcesService({ context }).updateSources({
            sourcesWithOperation,
            projectId: currentQnaMakerProjectStore.id
        });
    };

    return {
        getQnaMakerSources,
        addQnaMakerUrlSources,
        deleteQnaMakerSources,
        renameQnaMakerSources,
        addQnaMakerFileSources,
        refreshQnaMakerSourceURLs,
        addQnaMakerChitchatSources
    };
};
